import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Lock } from "lucide-react";
import PinPad from "../components/PinPad";
import { apiClient } from "../lib/api";
import { setAuth } from "../lib/auth";

export default function Login() {
  // Default landing = client login. Advisor mode is a discreet toggle at the bottom.
  const [role, setRole] = useState("client");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async () => {
    if (pin.length !== 4) {
      toast.error("Enter a 4-digit PIN");
      return;
    }
    setLoading(true);
    try {
      const { data } = await apiClient.post("/auth/login", { role, pin });
      setAuth(data);
      toast.success(`Welcome, ${data.name}`);
      nav(role === "advisor" ? "/advisor" : "/client", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
      setPin("");
    } finally {
      setLoading(false);
    }
  };

  const switchRole = (next) => {
    setRole(next);
    setPin("");
  };

  const isAdvisor = role === "advisor";

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-background relative overflow-hidden">
      {/* Subtle warm grid background */}
      <div className="pointer-events-none absolute inset-0 opacity-50"
           style={{
             backgroundImage:
               "linear-gradient(hsl(var(--border)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--border)) 1px, transparent 1px)",
             backgroundSize: "56px 56px",
             maskImage: "radial-gradient(ellipse at top left, black 25%, transparent 70%)",
             WebkitMaskImage: "radial-gradient(ellipse at top left, black 25%, transparent 70%)",
           }} />

      {/* Left brand panel */}
      <div className="hidden lg:flex lg:col-span-3 relative overflow-hidden border-r border-border">
        <div className="absolute -top-32 -left-32 h-[420px] w-[420px] rounded-full"
             style={{ background: "radial-gradient(circle, hsl(var(--saffron) / 0.18) 0%, transparent 60%)" }} />
        <div className="absolute bottom-0 right-0 h-[360px] w-[360px] rounded-full"
             style={{ background: "radial-gradient(circle, hsl(var(--navy) / 0.08) 0%, transparent 65%)" }} />
        <div className="relative z-10 flex flex-col justify-between p-12 xl:p-14 w-full">
          <div className="flex items-center gap-4">
            <img
              src="/brand/logo-transparent.png"
              alt="Shree Finserv"
              className="h-20 xl:h-24 w-auto object-contain"
            />
          </div>

          <div className="space-y-7 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-border bg-card/80 backdrop-blur rounded-full">
              <span className="h-1.5 w-1.5 rounded-full bg-saffron lime-pulse" />
              <span className="text-[10px] uppercase tracking-[0.3em] font-mono text-muted-foreground">
                Markets · Live
              </span>
            </div>
            <h1 className="font-display text-[60px] xl:text-[84px] leading-[0.95] tracking-tighter font-extrabold text-foreground">
              Portfolios that
              <br />
              <span className="text-saffron">compound,</span>
              <br />
              quietly.
            </h1>
            <p className="text-muted-foreground text-base leading-relaxed max-w-md">
              A purpose-built workspace for advisors and clients.
              Live benchmarks, fundamental intelligence, and unflinching transparency — in one ledger.
            </p>
            <div className="grid grid-cols-3 gap-3 max-w-md">
              {[
                ["NIFTY", "50"],
                ["SENSEX", "30"],
                ["NIFTY", "500"],
              ].map(([a, b]) => (
                <div key={a + b} className="card-noir p-4 rounded-lg">
                  <div className="text-[9px] text-muted-foreground tracking-[0.3em] uppercase font-mono">
                    Benchmark
                  </div>
                  <div className="font-display font-bold text-xl mt-1 text-foreground">
                    {a} <span className="text-saffron">{b}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-[10px] text-muted-foreground tracking-[0.32em] uppercase font-mono">
            NSE · BSE · ARN-193972
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="lg:col-span-2 flex items-center justify-center p-6 sm:p-12 relative z-10">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <img
              src="/brand/logo-transparent.png"
              alt="Shree Finserv"
              className="h-14 w-auto object-contain"
            />
          </div>

          <div className="mb-7">
            <div className="inline-flex items-center gap-1.5 text-[10px] text-saffron tracking-[0.3em] uppercase font-mono font-bold mb-3">
              <span className="h-1 w-1 rounded-full bg-saffron lime-pulse" />
              {isAdvisor ? "Advisor Access" : "Client Portal"}
            </div>
            <h2 className="font-display text-4xl font-extrabold tracking-tighter" data-testid="login-heading">
              {isAdvisor ? "Sign in." : "Welcome back."}
            </h2>
            <p className="text-sm text-muted-foreground mt-2">
              {isAdvisor
                ? "Restricted access. Enter your 4-digit PIN."
                : "Enter your 4-digit PIN to view your portfolio."}
            </p>
          </div>

          <PinPad value={pin} onChange={setPin} />

          <button
            data-testid="login-submit"
            onClick={submit}
            disabled={loading}
            className="mt-6 w-full bg-saffron hover:brightness-110 text-white py-3.5 text-sm font-bold uppercase tracking-[0.22em] rounded-md flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98] shadow-[0_4px_0_hsl(var(--saffron)/0.25)] transition-all"
          >
            {loading ? "Signing in…" : "Continue"}
            <ArrowRight className="h-4 w-4" strokeWidth={2} />
          </button>

          <p className="mt-5 text-[11px] text-muted-foreground font-mono">
            {isAdvisor
              ? "Advisor PIN issued by Shree Finserv."
              : "Your PIN was issued by your advisor."}
          </p>

          {/* Discreet role switch */}
          <div className="mt-10 pt-6 border-t border-border">
            {!isAdvisor ? (
              <button
                data-testid="switch-to-advisor"
                onClick={() => switchRole("advisor")}
                className="w-full text-[10px] uppercase tracking-[0.32em] text-muted-foreground hover:text-saffron flex items-center justify-center gap-2 font-mono font-semibold transition-colors"
              >
                <Lock className="h-3 w-3" strokeWidth={1.5} />
                Advisor login
              </button>
            ) : (
              <button
                data-testid="switch-to-client"
                onClick={() => switchRole("client")}
                className="w-full text-[10px] uppercase tracking-[0.32em] text-muted-foreground hover:text-saffron flex items-center justify-center gap-2 font-mono font-semibold transition-colors"
              >
                ← Back to client login
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
